import { useEffect, useMemo } from 'react';

import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import { dateSelectors } from 'app-components/flux/date';
import { rootUiActions } from 'app-components/flux/root-ui';
import { scheduledTransactionsSelectors, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import type { DaysOfWeekShort, RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import { FrequencyEnum } from 'app-components/flux/scheduled-transactions/frequency';
import { transactionsTypes } from 'app-components/flux/transactions';
import { getLogger } from 'app-components/utils/core';

import useScheduledTransactionEditDialog, {
  SCHEDULED_TRANSACTION_DIALOG,
} from 'components/ScheduledTransactions/ScheduledTransactionEditDialog/dispatcher';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import type RootState from 'app-components/utils/redux-store/rootState';

const log = getLogger('components/Dialogs/LlmRecurringDialog');

export const DIALOG_TYPE_LLM_RECURRING = 'LLM_RECURRING';

type UpsertRecurringData = {
  seriesId?: string;
  name?: string;
  amount?: number;
  dueOn?: string;
  accountId?: string;
  categoryId?: string;
  type?: string; // bill | subscription | income
};

type LlmRecurringDialogProps = {
  dialogId: string;
  operation: 'create' | 'update';
  data: UpsertRecurringData;
  callback: (result: { success: boolean; error?: string; seriesId?: string }) => void;
};

const buildDefaultRecurrence = (isIncome: boolean, currentDay: number): RecurrenceType => {
  return isIncome
    ? { byDay: ['FR' as DaysOfWeekShort], frequency: FrequencyEnum.WEEKLY, interval: 2 }
    : { byMonthDay: [currentDay || 1], frequency: FrequencyEnum.MONTHLY, interval: 1 };
};

const LlmRecurringDialog = ({ dialogId, operation, data, callback }: LlmRecurringDialogProps) => {
  const dispatch = useAppDispatch();
  const openScheduledDialog = useScheduledTransactionEditDialog();

  const hasSeriesId = Boolean(data.seriesId && data.seriesId.length > 0);
  const existingSeries = useAppSelector((state: RootState) =>
    hasSeriesId
      ? scheduledTransactionsSelectors.getScheduledTransactionById(state, data.seriesId as string)
      : undefined,
  );
  const currentDateString = useAppSelector(dateSelectors.getCurrentDateString);
  const currentDate = useAppSelector(dateSelectors.getCurrentDate);
  const scheduledTransaction = useMemo(() => {
    if (existingSeries) return existingSeries;

    const isIncome = data.type === 'income';
    const defaultRecurrence = buildDefaultRecurrence(isIncome, currentDate.day);
    const dueOnDate = data.dueOn ? DateTime.fromISO(data.dueOn) : currentDate;
    const dueOn: string = dueOnDate.isValid ? dueOnDate.toISODate() : currentDateString;
    const clientId = uuidv4().toUpperCase();

    const transaction = transactionsTypes.mkCashFlowTransaction({
      clientId,
      amount: data.amount ?? 0,
      accountId: data.accountId,
      payee: data.name,
      coa: data.categoryId ? { id: data.categoryId, type: 'CATEGORY' as const } : undefined,
      postedOn: dueOn,
      isBill: data.type === 'bill',
      isSubscription: data.type === 'subscription',
    });

    return scheduledTransactionsTypes.mkScheduledTransaction({
      id: undefined,
      clientId,
      transaction,
      type: (data.type || 'bill').toUpperCase(),
      recurrence: scheduledTransactionsTypes.mkRecurrence(defaultRecurrence),
      dueOn,
      excludeF2S: scheduledTransactionsTypes.STExcludeEnum.NONE,
      excludeReports: scheduledTransactionsTypes.STExcludeEnum.NONE,
      autoAdjustAmount: 'NONE',
      autoAdjustDueOn: false,
      isUserVerified: true,
    });
  }, [
    existingSeries,
    data.type,
    data.dueOn,
    data.amount,
    data.accountId,
    data.name,
    data.categoryId,
    currentDate,
    currentDateString,
  ]);

  useEffect(() => {
    log.debug('Closing due to user cancelling');
    const handleClose = () => {
      callback({ success: false, error: 'User cancelled' });
      dispatch(rootUiActions.removeDialog(SCHEDULED_TRANSACTION_DIALOG));
    };

    const handleSubmit = (updated?: { id?: string; clientId?: string }) => {
      log.debug('handling submit');
      const seriesId =
        updated?.id || updated?.clientId || scheduledTransaction.id || (scheduledTransaction as any).clientId;
      callback({ success: true, seriesId });
      dispatch(rootUiActions.removeDialog(SCHEDULED_TRANSACTION_DIALOG));
    };

    openScheduledDialog({
      open: true,
      title: operation === 'create' ? 'Create Recurring' : 'Update Recurring',
      scheduledTransaction,
      onClose: handleClose,
      onSubmit: handleSubmit,
      initialTab: 0,
    });

    // remove the LLM wrapper immediately after dispatching the real dialog
    dispatch(rootUiActions.removeDialog(dialogId));
  }, [callback, dialogId, dispatch, openScheduledDialog, operation, scheduledTransaction]);

  return null;
};

export default LlmRecurringDialog;
